import React, { useState, useEffect, useRef } from 'react';
import { 
    CheckCircle, AlertTriangle, Calendar, Phone, User, Printer, ArrowLeft 
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Sidebar from '../../components/Sidebar';
import '../Dashboard.css';

const DetailsReservation = () => {
  const navigate = useNavigate();
  const { groupeId } = useParams();

  const [groupe, setGroupe] = useState(null);
  const [lignesChambres, setLignesChambres] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal de Check-in
  const [checkInModal, setCheckInModal] = useState({ show: false, ligne: null });
  const [clientNomReel, setClientNomReel] = useState('');
  const [clientTelReel, setClientTelReel] = useState('');

  // Modal de Paiement
  const [paiementModal, setPaiementModal] = useState(false);
  const [montantPaiement, setMontantPaiement] = useState('');
  const [modePaiement, setModePaiement] = useState('ESPÈCES');
  const [referencePaiement, setReferencePaiement] = useState('');

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const printRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Reservation_${groupeId}`
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      if (window.electronAPI && window.electronAPI.getDetailsReservationGroupe) {
        const data = await window.electronAPI.getDetailsReservationGroupe(groupeId);
        if (data) {
          setGroupe(data.groupe);
          setLignesChambres(data.lignesChambres || []);
          setPaiements(data.paiements || []);
        }
      }
    } catch (err) {
      console.error("Erreur chargement détails réservation:", err);
      showToast("Erreur de chargement des détails", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupeId) {
      fetchData();
    }
  }, [groupeId]);

  const handleOpenCheckIn = (ligne) => {
    setCheckInModal({ show: true, ligne });
    setClientNomReel(ligne.client_nom || '');
    setClientTelReel(ligne.client_telephone || '');
  };

  const handleValiderCheckIn = async (e) => {
    e.preventDefault();
    if (!clientNomReel.trim()) {
      showToast("Le nom réel du client est obligatoire", "error");
      return;
    }

    try {
      if (window.electronAPI && window.electronAPI.validerCheckInChambre) {
        await window.electronAPI.validerCheckInChambre(checkInModal.ligne.id, {
          client_nom_reel: clientNomReel.trim(),
          client_telephone_reel: clientTelReel.trim()
        });
      }
      showToast("Check-in validé avec succès !");
      setCheckInModal({ show: false, ligne: null });
      fetchData();
    } catch (err) {
      console.error("Erreur check-in:", err);
      showToast("Erreur lors de la validation du check-in", "error");
    }
  };

  const handleAjouterPaiement = async (e) => {
    e.preventDefault();
    const montant = parseFloat(montantPaiement);
    if (!montant || montant <= 0) {
      showToast("Veuillez saisir un montant valide", "error");
      return;
    }

    try {
      if (window.electronAPI && window.electronAPI.ajouterPaiementHebergement) {
        await window.electronAPI.ajouterPaiementHebergement({
          groupe_id: groupeId,
          montant,
          mode_paiement: modePaiement,
          reference: referencePaiement
        });
      }
      showToast("Paiement enregistré avec succès");
      setPaiementModal(false);
      setMontantPaiement('');
      setReferencePaiement('');
      fetchData();
    } catch (err) {
      console.error("Erreur paiement:", err);
      showToast("Erreur lors de l'enregistrement du paiement", "error");
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', position: 'relative' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button onClick={() => navigate(-1)} style={backBtnStyle}><ArrowLeft size={16} /> Retour</button>
            <div>
              <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900' }}>
                DOSSIER DE RÉSERVATION : #{groupeId}
              </h1>
              <div style={{ color: '#64748b', fontSize: '12px' }}>
                Réservant : <strong>{groupe?.reservant_nom}</strong> | Statut Global : <span style={{ color: '#2563eb', fontWeight: 'bold' }}>{groupe?.statut}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => setPaiementModal(true)} style={btnPrimaryStyle}>
              + Encaisser un Paiement
            </button>
            <button onClick={handlePrint} style={{ ...btnPrimaryStyle, background: '#475569' }}>
              <Printer size={16} /> Imprimer Fiche
            </button>
          </div>
        </header>

        <div style={{ padding: '30px 40px' }}>
          
          {/* Cartes récapitulatives financières */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' }}>
            <div style={cardInfoStyle}>
              <span style={labelStyle}>MONTANT TOTAL GROUPE</span>
              <h2 style={{ margin: '5px 0 0 0', color: '#0f172a' }}>{Number(groupe?.montant_total_groupe || 0).toLocaleString()} F</h2>
            </div>
            <div style={cardInfoStyle}>
              <span style={labelStyle}>MONTANT PAYÉ</span>
              <h2 style={{ margin: '5px 0 0 0', color: '#10b981' }}>{Number(groupe?.montant_paye || 0).toLocaleString()} F</h2>
            </div>
            <div style={cardInfoStyle}>
              <span style={labelStyle}>RESTE À PAYER</span>
              <h2 style={{ margin: '5px 0 0 0', color: '#ef4444' }}>{Number(groupe?.reste_a_payer || 0).toLocaleString()} F</h2>
            </div>
          </div>

          {/* Tableau des Chambres et Occupants */}
          <div style={{ ...contentCardStyle, marginBottom: '30px' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', fontWeight: '800', fontSize: '14px' }}>
              Chambres Rattachées & Suivi des Check-in / Check-out
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Chambre</th>
                  <th style={thStyle}>Occupant / Client Réel</th>
                  <th style={thStyle}>Statut Chambre</th>
                  <th style={thStyle}>Tarif Nuitée</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions / Check-in</th>
                </tr>
              </thead>
              <tbody>
                {lignesChambres.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      Aucune chambre rattachée.
                    </td>
                  </tr>
                ) : (
                  lignesChambres.map((ligne) => (
                    <tr key={ligne.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...tdStyle, fontWeight: '800' }}>
                        <code style={codeStyle}>{ligne.numero_chambre || 'Chambre'}</code>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '700' }}>{ligne.client_nom}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{ligne.client_telephone || 'Pas de tél'}</div>
                      </td>
                      <td style={tdStyle}>
                        <span style={badgeStyle(ligne.statut)}>{ligne.statut}</span>
                      </td>
                      <td style={tdStyle}>{Number(ligne.tarif_nuit || 0).toLocaleString()} F</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {ligne.statut === 'RESERVATION' && (
                          <button onClick={() => handleOpenCheckIn(ligne)} style={actionBtnStyle('#10b981')}>
                            <CheckCircle size={14}/> Faire Check-in
                          </button>
                        )}
                        {ligne.statut === 'CHECK_IN' && (
                          <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '800' }}>Séjour en cours</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Tableau des Paiements */}
          <div style={contentCardStyle}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', fontWeight: '800', fontSize: '14px' }}>
              Historique des Règlements
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Date & Heure</th>
                  <th style={thStyle}>Mode de Paiement</th>
                  <th style={thStyle}>Référence</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Montant</th>
                </tr>
              </thead>
              <tbody>
                {paiements.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      Aucun paiement enregistré pour l'instant.
                    </td>
                  </tr>
                ) : (
                  paiements.map((p) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>{new Date(p.date_paiement).toLocaleString('fr-FR')}</td>
                      <td style={{ ...tdStyle, fontWeight: '700' }}>{p.mode_paiement}</td>
                      <td style={tdStyle}>{p.reference || '---'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '800', color: '#10b981' }}>
                        {Number(p.montant).toLocaleString()} F
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </div>
      </main>

      {/* Modal Check-In */}
      {checkInModal.show && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h2 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '800' }}>Validation Check-In (Arrivée Client)</h2>
            <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '15px' }}>
              Le client réel se présente. Modifiez si besoin son nom et son numéro de téléphone.
            </p>
            <form onSubmit={handleValiderCheckIn} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={labelStyle}>NOM RÉEL DU CLIENT</label>
                <input 
                  type="text" 
                  style={inputStyle} 
                  value={clientNomReel} 
                  onChange={(e) => setClientNomReel(e.target.value)} 
                  required 
                />
              </div>
              <div>
                <label style={labelStyle}>TÉLÉPHONE DU CLIENT</label>
                <input 
                  type="text" 
                  style={inputStyle} 
                  value={clientTelReel} 
                  onChange={(e) => setClientTelReel(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" onClick={() => setCheckInModal({ show: false, ligne: null })} style={btnSecondaryStyle}>Annuler</button>
                <button type="submit" style={btnSubmitStyle}>Valider le Check-in</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Paiement */}
      {paiementModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h2 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '800' }}>Enregistrer un Encaissement</h2>
            <form onSubmit={handleAjouterPaiement} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={labelStyle}>MONTANT (FCFA)</label>
                <input 
                  type="number" 
                  style={inputStyle} 
                  value={montantPaiement} 
                  onChange={(e) => setMontantPaiement(e.target.value)} 
                  required 
                />
              </div>
              <div>
                <label style={labelStyle}>MODE DE PAIEMENT</label>
                <select 
                  style={inputStyle} 
                  value={modePaiement} 
                  onChange={(e) => setModePaiement(e.target.value)}
                >
                  <option value="ESPÈCES">ESPÈCES</option>
                  <option value="TMONEY">TMONEY</option>
                  <option value="FLOOZ">FLOOZ</option>
                  <option value="CARTE BANCAIRE">CARTE BANCAIRE</option>
                  <option value="VIREMENT">VIREMENT</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>RÉFÉRENCE / TRANSACTION</label>
                <input 
                  type="text" 
                  style={inputStyle} 
                  placeholder="N° de reçu ou transaction..."
                  value={referencePaiement} 
                  onChange={(e) => setReferencePaiement(e.target.value)} 
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" onClick={() => setPaiementModal(false)} style={btnSecondaryStyle}>Annuler</button>
                <button type="submit" style={btnSubmitStyle}>Encaisser</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Composant Masqué pour Impression */}
      <div style={{ display: 'none' }}>
        <div ref={printRef} style={{ padding: '30px', fontFamily: 'Segoe UI, sans-serif', color: '#0f172a' }}>
          <h2>FICHE DE RÉSERVATION - LEDI EXPERT PRO</h2>
          <p><strong>Dossier :</strong> #{groupe?.id}</p>
          <p><strong>Réservant :</strong> {groupe?.reservant_nom} ({groupe?.reservant_telephone})</p>
          <p><strong>Période :</strong> Du {groupe?.date_arrivee_prevue} au {groupe?.date_depart_prevue}</p>
          <hr />
          <h3>Chambres :</h3>
          <ul>
            {lignesChambres.map(l => (
              <li key={l.id}>{l.numero_chambre || 'Chambre'} - Occupant : {l.client_nom} ({l.statut})</li>
            ))}
          </ul>
        </div>
      </div>

      <style>{`@keyframes slideInToast { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' };
const cardInfoStyle = { background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' };
const btnPrimaryStyle = { background: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const btnSecondaryStyle = { border: '1px solid #e2e8f0', padding: '10px 15px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569', background: 'white' };
const backBtnStyle = { background: 'none', border: 'none', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' };
const inputStyle = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', marginTop: '5px', background: '#fff' };
const labelStyle = { fontSize: '11px', fontWeight: '900', color: '#475569' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const codeStyle = { fontSize: '12px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', color: '#2563eb', fontWeight: '900' };
const btnSubmitStyle = { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' };
const actionBtnStyle = (color) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', border: `1px solid ${color}30`, background: `${color}10`, color: color, fontSize: '11px', fontWeight: '900', cursor: 'pointer' });
const badgeStyle = (statut) => {
  let bg = '#dcfce7', color = '#15803d';
  if (statut === 'RESERVATION') { bg = '#e0f2fe'; color = '#0369a1'; }
  else if (statut === 'ANNULEE' || statut === 'CHECK_OUT') { bg = '#fee2e2'; color = '#b91c1c'; }
  return { padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900', background: bg, color: color };
};
const toastContainerStyle = { position: 'fixed', top: '20px', right: '20px', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', padding: '30px', borderRadius: '12px', width: '450px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };

export default DetailsReservation;